import type { Column, ColumnColor, Priority, Task, User } from './types.ts'

export interface ApiErrorBody {
  error: string
  attemptsLeft?: number
  retryAt?: number
  task?: Task
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.error)
    this.name = 'ApiError'
  }

  /** A sentence for the screen. See errorMessage() for why this is not `body.error`. */
  get message_pl(): string {
    return errorMessage(this)
  }
}

/**
 * Maps a server error code to something a person can act on (ui-spec 7: what happened and what to
 * do, no apology).
 *
 * The server deliberately answers with codes and never with prose — the text of a database error is
 * written by the database, and putting it on screen is how a 500 becomes a phishing surface
 * (apps/server/src/routes/errors.ts). So the sentences live here, on the side that knows the user.
 *
 * The fallback is not the raw code: `invalid_reference` on screen tells nobody anything, and a code
 * we forgot to translate should read as a bug in us, not as a message to the user.
 */
const MESSAGES: Record<string, string> = {
  invalid_body: 'Formularz ma niepoprawne dane — sprawdź pola i spróbuj ponownie.',
  invalid_params: 'Ten adres jest niepoprawny.',
  invalid_query: 'Filtry mają niepoprawną wartość — wyczyść je i spróbuj ponownie.',
  invalid_reference: 'Coś, do czego to zadanie się odwołuje, już nie istnieje. Odśwież stronę.',
  column_not_found: 'Ta kolumna już nie istnieje. Odśwież stronę.',
  assignee_not_found: 'Ta osoba już nie istnieje. Odśwież stronę.',
  note_not_found: 'Ta notatka już nie istnieje. Odśwież stronę.',
  task_not_found: 'To zadanie już nie istnieje.',
  not_found: 'Nie ma już czego pokazać — to zostało usunięte.',
  conflict: 'Ktoś zmienił to przed chwilą. Odśwież, żeby zobaczyć aktualną wersję.',
  payload_too_large: 'Treść jest za długa, żeby ją zapisać. Skróć ją i spróbuj ponownie.',
  unsupported_media_type: 'Nieobsługiwany format danych.',
  unauthorized: 'Twoja sesja wygasła. Zaloguj się ponownie.',
  rate_limited: 'Za dużo prób. Odczekaj chwilę.',
  internal_error: 'Coś się popsuło po naszej stronie. Spróbuj ponownie za chwilę.',
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return MESSAGES[err.body.error] ?? MESSAGES.internal_error!
  }
  // Anything that is not an ApiError never reached the server — DNS, offline, a dropped socket.
  return 'Brak połączenia z serwerem. Sprawdź sieć i spróbuj ponownie.'
}

/**
 * Thin fetch wrapper. `credentials: 'same-origin'` is what carries the session cookie — without it
 * every request looks anonymous and the app sits on the login screen forever.
 *
 * Content-Type is set only when there is a body: Fastify rejects an empty body that claims to be
 * JSON, which would break every no-body POST (archive, logout).
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let body: ApiErrorBody = { error: `http_${res.status}` }
    try {
      body = (await res.json()) as ApiErrorBody
    } catch {
      // Non-JSON error body (proxy, nginx). Keep the synthesized one.
    }
    throw new ApiError(res.status, body)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const authApi = {
  me: () => api<{ user: User }>('/api/me'),
  login: (email: string, password: string) =>
    api<{ user: User }>('/api/login', { method: 'POST', ...json({ email, password }) }),
  logout: () => api<{ ok: true }>('/api/logout', { method: 'POST' }),
}

export interface TaskFilters {
  columnId?: number
  assigneeId?: number
  archived?: boolean
  scope?: 'active' | 'open' | 'closed' | 'all'
  q?: string
  label?: string
}

function query(filters: TaskFilters): string {
  const params = new URLSearchParams()
  if (filters.columnId !== undefined) params.set('columnId', String(filters.columnId))
  if (filters.assigneeId !== undefined) params.set('assigneeId', String(filters.assigneeId))
  if (filters.archived !== undefined) params.set('archived', String(filters.archived))
  if (filters.scope !== undefined) params.set('scope', filters.scope)
  if (filters.q) params.set('q', filters.q)
  if (filters.label) params.set('label', filters.label)
  const s = params.toString()
  return s ? `?${s}` : ''
}

export interface TaskPatch {
  title?: string
  descriptionMd?: string
  priority?: Priority
  dueDate?: string | null
  assigneeId?: number | null
  labels?: string[]
  columnId?: number
  /** Optimistic lock; omit to force the write. */
  updatedAt?: string
}

export const tasksApi = {
  list: (filters: TaskFilters = {}) => api<{ tasks: Task[] }>(`/api/tasks${query(filters)}`),
  get: (id: number) => api<{ task: Task }>(`/api/tasks/${id}`),
  create: (input: { title: string; columnId?: number }) =>
    api<{ task: Task }>('/api/tasks', { method: 'POST', ...json(input) }),
  patch: (id: number, patch: TaskPatch) =>
    api<{ task: Task }>(`/api/tasks/${id}`, { method: 'PATCH', ...json(patch) }),
  // Position is computed server-side; we only ever say "relative to what" (ADR 0005).
  move: (id: number, input: { columnId: number; beforeTaskId?: number; afterTaskId?: number }) =>
    api<{ task: Task }>(`/api/tasks/${id}/move`, { method: 'POST', ...json(input) }),
  archive: (id: number) => api<{ task: Task }>(`/api/tasks/${id}/archive`, { method: 'POST' }),
  unarchive: (id: number) => api<{ task: Task }>(`/api/tasks/${id}/unarchive`, { method: 'POST' }),
}

export interface Note {
  id: number
  title: string
  updatedAt: string
  updatedBy: number | null
}

/**
 * Metadata only — a note's body never travels over REST, it moves over /collab (ADR 0002).
 *
 * This used to live inside NotesView. It moved here when the task drawer needed the same list to
 * offer a note to attach: two components fetching `/api/notes` through two hand-written clients is
 * how one of them ends up on a stale contract.
 */
export const notesApi = {
  list: () => api<{ notes: Note[] }>('/api/notes'),
  create: (title: string) =>
    api<{ note: Note }>('/api/notes', { method: 'POST', ...json({ title }) }),
  rename: (id: number, title: string) =>
    api<{ note: Note }>(`/api/notes/${id}`, { method: 'PATCH', ...json({ title }) }),
  remove: (id: number) => api<void>(`/api/notes/${id}`, { method: 'DELETE' }),
}

/** A note attached to a task, and a task a note is attached to (ADR 0012). */
export interface LinkedNote {
  id: number
  title: string
  updatedAt: string
  updatedBy: number | null
  linkedAt: string
  linkedBy: number | null
}

export interface LinkedTask {
  id: number
  ref: string
  title: string
  columnId: number
  archivedAt: string | null
  linkedAt: string
  linkedBy: number | null
}

/**
 * Linking lives under the task on both ends, including the button that sits on the note screen.
 * One write path means the two screens cannot drift into two different ideas of what a link is.
 */
export const taskNotesApi = {
  list: (taskId: number) => api<{ notes: LinkedNote[] }>(`/api/tasks/${taskId}/notes`),
  link: (taskId: number, noteId: number) =>
    api<{ notes: LinkedNote[] }>(`/api/tasks/${taskId}/notes`, {
      method: 'POST',
      ...json({ noteId }),
    }),
  unlink: (taskId: number, noteId: number) =>
    api<void>(`/api/tasks/${taskId}/notes/${noteId}`, { method: 'DELETE' }),
  forNote: (noteId: number) => api<{ tasks: LinkedTask[] }>(`/api/notes/${noteId}/tasks`),
}

export const columnsApi = {
  list: () => api<{ columns: Column[] }>('/api/columns'),
  create: (input: { name: string; color?: ColumnColor }) =>
    api<{ column: Column }>('/api/columns', { method: 'POST', ...json(input) }),
}

export const usersApi = {
  list: () => api<{ users: User[] }>('/api/users'),
}

export interface ApiTokenInfo {
  id: number
  label: string
  createdAt: string
  lastUsedAt: string | null
}

/**
 * `create` is the only response that ever carries the raw value, and only this once — the server
 * keeps a sha256 (PRD §4.5). If the UI loses it before the person copies it, it is gone.
 */
export const tokensApi = {
  list: () => api<{ tokens: ApiTokenInfo[] }>('/api/tokens'),
  create: (label: string) =>
    api<{ token: string; tokenInfo: ApiTokenInfo }>('/api/tokens', {
      method: 'POST',
      ...json({ label }),
    }),
  revoke: (id: number) => api<void>(`/api/tokens/${id}`, { method: 'DELETE' }),
}

export const profileApi = {
  rename: (displayName: string) =>
    api<{ user: User }>('/api/me', { method: 'PATCH', ...json({ displayName }) }),
}

export interface AppNotification {
  id: number
  kind: 'mention'
  actorId: number | null
  taskId: number | null
  taskRef: string | null
  taskTitle: string | null
  noteId: number | null
  noteTitle: string | null
  commentId: number | null
  excerpt: string
  readAt: string | null
  createdAt: string
}

export const notificationsApi = {
  list: () => api<{ notifications: AppNotification[]; unread: number }>('/api/notifications'),
  /** No id marks everything read. */
  markRead: (id?: number) =>
    api<{ marked: number; unread: number }>('/api/notifications/read', {
      method: 'POST',
      ...json(id === undefined ? {} : { id }),
    }),
}
